import { useCallback, useMemo } from 'react';

type UpdateParams = (updates: Record<string, string | null>) => void;

/**
 * Derives a string[] from a single comma-separated URL search param and
 * provides a stable setter that writes through to the URL. The URL is the
 * single source of truth — there is no local useState mirror.
 *
 * WHY the old two-way mirror oscillated:
 *   The previous useUrlArrayParamSync kept a local useState copy alongside two
 *   effects: one writing local→URL and one writing URL→local. When a third
 *   writer (the deep-link jump effect in WorkQueue) cleared the param from
 *   outside the hook, both effects saw changed deps in the same commit:
 *   effect 1 pushed the stale local value back to the URL, effect 2 cleared
 *   local to match the now-empty URL. The two writers then traded values on
 *   every render, creating an infinite swap oscillator.
 *
 *   With a single source of truth there is only one writer per param, so the
 *   oscillation is structurally impossible.
 *
 * The array is memoized on the raw string from the URL (not on the
 * URLSearchParams object reference), so the returned array identity is stable
 * whenever the param's content is unchanged, even if unrelated params (e.g.
 * ?case= or ?step=) change. Stable identity matters for downstream dep arrays
 * (page-reset effects, React Query keys).
 */
/**
 * The list stays comma-separated in the URL, but a value that itself contains
 * a comma ("Christine Bloss, DDS") is escaped first - otherwise it split into
 * two bogus filter values and matched nothing. Legacy URLs and saved filters
 * hold no %2C, so they keep decoding exactly as before.
 */
const ESCAPED_COMMA = '%2C';

export function encodeUrlArray(values: string[]): string {
    return values.map((v) => v.replaceAll(',', ESCAPED_COMMA)).join(',');
}

export function decodeUrlArray(raw: string): string[] {
    return raw
        .split(',')
        .map((s) => s.trim().replaceAll(ESCAPED_COMMA, ','))
        .filter(Boolean);
}

export function useUrlArrayParam(
    paramKey: string,
    searchParams: URLSearchParams,
    updateParams: UpdateParams
): [string[], (next: string[]) => void] {
    const raw = searchParams.get(paramKey) ?? '';

    const value = useMemo(() => decodeUrlArray(raw), [raw]);

    const setValue = useCallback(
        (next: string[]) => updateParams({ [paramKey]: encodeUrlArray(next) || null }),
        [paramKey, updateParams]
    );

    return [value, setValue];
}
